import { isBadAttribute } from './bad_elements';
import { CSSRewriter } from './cssrewriter';
import { NormalizedAttributeName, NormalizedTagName } from './normalizedstring';
import { UrlRewriter } from '@fullstory/url-rewriter';
import { splitSrcset } from '@fullstory/shared/domutils';

export type NodeLike = {
  tag: NormalizedTagName;
};

// Utility class for rewriting bits of nodes as they're reified for playback.
export class NodeRewriter {
  constructor(
    private _cssRewriter: CSSRewriter,
    private _urlRewriter: UrlRewriter,
    /*
      This is a temporary feature flag check to
      safely modify a regex expression to support srcSet descriptors
     */
    private _srcsetDescriptorsEnabled: boolean = false,
  ) { }

  // Rewrites the value of an attribute to be set on a node and returns an
  // action for the caller to take; see AttrRewriteAction for details.
  rewriteAttrForNode(
    node: NodeLike | null,
    attr: NormalizedAttributeName,
    value: string | null,
  ): string | null {
    if (node == null) {
      return null;
    }
    return this.rewriteAttr(node.tag, attr, value);
  }

  // Rewrites the value of an attribute, given the type of element it will be
  // applied to and the base URL of the owning document.
  rewriteAttr(
    tag: NormalizedTagName,
    attr: NormalizedAttributeName,
    value: string | null,
  ): string | null {
    if (attr == null || value == null || isBadAttribute(tag, attr)) {
      return null;
    }

    if (attr == 'style') {
      return this._cssRewriter.rewriteStyleAttributeValue(value);
    }

    switch (tag) {
      case 'img':
        switch (attr) {
          case 'src':
            return this._urlRewriter.rewriteUrl(value);
          case 'srcset': {
            const rewrittenSrcSet = this.rewriteSrcSet(value);
            return rewrittenSrcSet;
          }
        }
        break;
      case 'source':
        switch (attr) {
          case 'srcset': {
            const rewrittenSrcSet = this.rewriteSrcSet(value);
            return rewrittenSrcSet;
          }
        }
        break;
      case 'link':
        switch (attr) {
          case 'href':
            return this._urlRewriter.rewriteUrl(value);
          case 'rel':
            return this.rewriteLinkRel(value);
        }
        break;
      case 'meta':
        switch (attr) {
          case 'name':
            return this.rewriteMetaName(value);
        }
        break;
      case 'use':
        switch (attr) {
          case 'href':
          case 'xlink:href':
            return this.rewriteExternalResourceUrl(value);
        }
        break;
      case 'image':
        switch (attr) {
          case 'href':
          case 'xlink:href':
            return this._urlRewriter.rewriteUrl(value);
        }
        break;
      case 'embed':
        switch (attr) {
          case 'src':
            return this._urlRewriter.rewriteUrl(value);
          case 'type':
            return this.rewriteObjectOrEmbedType(tag, value);
        }
        break;
      case 'object':
        switch (attr) {
          case 'data':
            return this._urlRewriter.rewriteUrl(value);
          case 'type':
            return this.rewriteObjectOrEmbedType(tag, value);
        }
        break;
      case 'html':
        switch (attr) {
          case 'class':
            return this.rewriteRootElementClass(value);
        }
        break;
    }

    return value;
  }

  // Rewrite <use> references to SVG resource documents. These need to be
  // loaded over XHR, because <use> doesn't have CORS support. Moreover, SVG
  // documents can contain scripts and other dangerous content, so we need to
  // rewrite them before presenting them to the user. Rather than try to
  // accomplish all of that here, we return enough information for the caller
  // to deal with the problem if they so choose.
  rewriteExternalResourceUrl(url: string): string | null {
    // The playback service worker does most of the work here, but we do need to
    // make sure that we generate a same-origin URL, because <use> does not
    // support CORS.
    // TODO(sethf): We should probably rewrite data URIs here in such a way that
    // the playback service worker gets a crack at those, too.
    return this._urlRewriter.rewriteUrl(url, { requiresSameOrigin: true });
  }

  // Keep roughly in sync with similar code in fs/cook/cookpage/extractors/resources.go.
  private rewriteSrcSet(srcset: string): string {
    if (srcset.match(/^\s*$/) != null) {
      return '';
    } // Empty srcset

    return splitSrcset(srcset, this._srcsetDescriptorsEnabled)
      .map(p => {
        const urlAndRez = p.trim().split(/\s+/);
        if (urlAndRez.length > 2 || urlAndRez.length < 1) {
          console.log(`Weird srcset component: ${p}`);
          return p;
        }

        // Rewrite the srcset part.
        const rewrittenPart = this._urlRewriter.rewriteUrl(urlAndRez[0] ?? '');
        if (rewrittenPart === '') {
          console.log(`Invalid URL for srcset component: ${p}`);
          return p;
        }

        if (urlAndRez[1]) {
          return `${rewrittenPart} ${urlAndRez[1]}`;
        }

        return rewrittenPart;
      })
      .join(', ');
  }

  private rewriteRootElementClass(classAttrValue: string): string {
    const classes = classAttrValue.split(/\s/);

    // If Modernizr's CSS Grid feature detection was used on the recorded
    // page, then we expect to find two classes on the root element:
    // '[no-]cssgrid' indicates whether the modern CSS Grid syntax was
    // supported (most browsers), and '[no-]cssgridlegacy' indicates whether
    // the old syntax was supported (IE 11). All browsers we support for
    // playback only allow the modern syntax, so we need to rewrite those
    // classes to 'cssgrid' and 'no-cssgridlegacy' so that the CSS rules the
    // recorded page provided for the modern syntax are used.
    // NOTE(seth): Modernizr can be configured to prefix these class names with
    // a user-specified string; might be worth supporting at some point.
    let cssGridFeatureDetected: boolean | undefined = undefined;
    let cssGridLegacyFeatureDetected: boolean | undefined = undefined;
    const rewrittenClasses = classes.map(className => {
      switch (className) {
        case 'cssgrid':
          cssGridFeatureDetected = true;
          return 'cssgrid';
        case 'no-cssgrid':
          cssGridFeatureDetected = false;
          return 'cssgrid';
        case 'cssgridlegacy':
          cssGridLegacyFeatureDetected = true;
          return 'no-cssgridlegacy';
        case 'no-cssgridlegacy':
          cssGridLegacyFeatureDetected = false;
          return 'no-cssgridlegacy';
      }
      return className;
    });

    // If we didn't find both of the feature detection classes (which suggests
    // that whatever we found might not come from Modernizr), or if neither
    // form of CSS Grid was supported at recording time (in which case
    // enabling support could cause a major divergence during playback), don't
    // rewrite anything.
    if (cssGridFeatureDetected === undefined || cssGridLegacyFeatureDetected === undefined) {
      return classAttrValue;
    }
    if (!cssGridFeatureDetected && !cssGridLegacyFeatureDetected) {
      return classAttrValue;
    }

    return rewrittenClasses.join(' ');
  }

  private rewriteObjectOrEmbedType(tagName: string, typeName: string): string | null {
    // Images are OK.
    if (typeName.startsWith('image')) {
      return typeName;
    }

    // Anything else is suspicious, since it should've already be rejected by
    // the recording script. This indicates either a bug or that someone
    // maliciously injected a sketchy <object> or <embed> into the DOM by
    // talking to /rec/bundle directly. (The console error doesn't include the
    // latter case to minimize the chance for customer panic.)
    console.error(
      `Detected a non-image <${tagName}> element in the recorded ` +
      'DOM. This indicates a bug in the recording script.',
    );
    return null;
  }

  private rewriteMetaName(name: string): string | null {
    switch (name.toLowerCase()) {
      case 'referrer':
        // This should be controlled by our CSP policy, rather than by the
        // recorded page.
        return null;
      default:
        return name;
    }
  }

  private rewriteLinkRel(rel: string): string | null {
    if (rel === '') {
      return rel;
    }

    // Disallow non-stylesheet <link> types, which we don't need for replay.
    // And explicitly disallow rel="import", which is a security risk.
    const isRelStylesheet = /(?:^|\s)stylesheet(?:$|\s)/i.test(rel);
    const isRelImport = /(?:^|\s)import(?:$|\s)/i.test(rel);
    if (isRelStylesheet && !isRelImport) {
      return rel;
    }

    return null;
  }
}
